import { useTheme } from '@emotion/react';
import cn from 'classnames';

import { EColorMap } from '../constants/colorMap';
import { IConDefaultType } from '../types';

export enum CloseIconVariant {
  DEFAULT = 'default',
  SMALL = 'small',
  DELETE = 'delete',
  CLEAR = 'clear',
  DELETE_DARK = 'delete_dark',
  DELETE_LIGHT = 'delete_light',
  INPUT_CLEAR = 'input_clear',
}

interface ICloseIconProps extends IConDefaultType {
  variant?: CloseIconVariant;
}

function CloseIcon({
  color,
  className,
  size = 24,
  rotate,
  flip,
  variant = CloseIconVariant.DEFAULT,
  style,
  onClick,
}: ICloseIconProps) {
  const theme = useTheme();

  return (
    <i
      onClick={onClick}
      className={cn('dds-icon', className, { flip })}
      style={{
        width: size,
        minWidth: size,
        height: size,
        transform: `rotate(${rotate}deg)`,
        ...style,
      }}
    >
      {variant === CloseIconVariant.DEFAULT && (
        <svg viewBox="0 0 24 24" style={{ width: size }} fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            transform="translate(4.2929 4.2929) scale(1.44508)"
            d="M9.36614 0.22316C9.66369 -0.0743866 10.146 -0.0743866 10.4435 0.22316C10.741 0.520711 10.7411 1.00303 10.4435 1.30056L6.41074 5.33335L10.4435 9.36614C10.741 9.66369 10.7411 10.146 10.4435 10.4435C10.146 10.7411 9.66369 10.741 9.36614 10.4435L5.33335 6.41074L1.30056 10.4435C1.00303 10.7411 0.520711 10.741 0.22316 10.4435C-0.0743866 10.146 -0.0743866 9.66369 0.22316 9.36614L4.25595 5.33335L0.22316 1.30056C-0.0743866 1.00301 -0.0743866 0.520706 0.22316 0.22316C0.520706 -0.0743866 1.00301 -0.0743866 1.30056 0.22316L5.33335 4.25595L9.36614 0.22316Z"
            fill={color || EColorMap.gray_0}
          />
        </svg>
      )}
      {variant === CloseIconVariant.SMALL && (
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M0.863231 0.859325C1.12358 0.598975 1.54569 0.598975 1.80604 0.859325L11.1394 10.1927C11.3997 10.453 11.3997 10.8751 11.1394 11.1355C10.879 11.3958 10.4569 11.3958 10.1966 11.1355L0.863231 1.80213C0.602881 1.54178 0.602881 1.11967 0.863231 0.859325Z"
            fill={color || EColorMap.gray_0}
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M11.1394 0.859325C11.3997 1.11967 11.3997 1.54178 11.1394 1.80213L1.80604 11.1355C1.54569 11.3958 1.12358 11.3958 0.863231 11.1355C0.602881 10.8751 0.602881 10.453 0.863231 10.1927L10.1966 0.859325C10.4569 0.598975 10.879 0.598975 11.1394 0.859325Z"
            fill={color || EColorMap.gray_0}
          />
        </svg>
      )}
      {variant === CloseIconVariant.CLEAR && (
        <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            opacity="0.5"
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10 18C5.6 18 2 14.4 2 10C2 5.6 5.6 2 10 2C14.4 2 18 5.6 18 10C18 14.4 14.4 18 10 18ZM7.56569 6.43483C7.25327 6.12241 6.74673 6.12241 6.43431 6.43483C6.12189 6.74725 6.12189 7.25378 6.43431 7.5662L8.86885 10.0007L6.43439 12.4352C6.12198 12.7476 6.12198 13.2541 6.43439 13.5666C6.74681 13.879 7.25335 13.879 7.56577 13.5666L10.0002 11.1321L12.4342 13.5661C12.7466 13.8785 13.2532 13.8785 13.5656 13.5661C13.878 13.2537 13.878 12.7471 13.5656 12.4347L11.1316 10.0007L13.5657 7.56666C13.8781 7.25424 13.8781 6.74771 13.5657 6.43529C13.2532 6.12287 12.7467 6.12287 12.4343 6.43529L10.0002 8.86936L7.56569 6.43483Z"
            fill="#ADB0B8"
          />
        </svg>
      )}
      {variant === CloseIconVariant.DELETE && (
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
          <path
            opacity="0.5"
            fillRule="evenodd"
            clipRule="evenodd"
            d="M9.00078 16.1998C5.04078 16.1998 1.80078 12.9598 1.80078 8.9998C1.80078 5.0398 5.04078 1.7998 9.00078 1.7998C12.9608 1.7998 16.2008 5.0398 16.2008 8.9998C16.2008 12.9598 12.9608 16.1998 9.00078 16.1998ZM6.8099 5.79115C6.52872 5.50997 6.07284 5.50997 5.79166 5.79115C5.51049 6.07233 5.51049 6.5282 5.79166 6.80938L7.98275 9.00046L5.79174 11.1915C5.51056 11.4727 5.51056 11.9285 5.79174 12.2097C6.07291 12.4909 6.52879 12.4909 6.80997 12.2097L9.00098 10.0187L11.1916 12.2093C11.4727 12.4905 11.9286 12.4905 12.2098 12.2093C12.491 11.9281 12.491 11.4722 12.2098 11.1911L10.0192 9.00046L12.2099 6.8098C12.4911 6.52862 12.4911 6.07274 12.2099 5.79157C11.9287 5.51039 11.4728 5.51039 11.1916 5.79157L9.00098 7.98223L6.8099 5.79115Z"
            fill={color}
          />
        </svg>
      )}
      {variant === CloseIconVariant.DELETE_DARK && (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path
            opacity="0.5"
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10 18C5.6 18 2 14.4 2 10C2 5.6 5.6 2 10 2C14.4 2 18 5.6 18 10C18 14.4 14.4 18 10 18ZM7.56569 6.43483C7.25327 6.12241 6.74673 6.12241 6.43431 6.43483C6.12189 6.74725 6.12189 7.25378 6.43431 7.5662L8.86885 10.0007L6.43439 12.4352C6.12198 12.7476 6.12198 13.2541 6.43439 13.5666C6.74681 13.879 7.25335 13.879 7.56577 13.5666L10.0002 11.1321L12.4342 13.5661C12.7466 13.8785 13.2532 13.8785 13.5656 13.5661C13.878 13.2537 13.878 12.7471 13.5656 12.4347L11.1316 10.0007L13.5657 7.56666C13.8781 7.25424 13.8781 6.74771 13.5657 6.43529C13.2532 6.12287 12.7467 6.12287 12.4343 6.43529L10.0002 8.86936L7.56569 6.43483Z"
            fill="#676B8A"
          />
        </svg>
      )}
      {variant === CloseIconVariant.DELETE_LIGHT && (
        <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10.5 18.0005C6.1 18.0005 2.5 14.4005 2.5 10.0005C2.5 5.60049 6.1 2.00049 10.5 2.00049C14.9 2.00049 18.5 5.60049 18.5 10.0005C18.5 14.4005 14.9 18.0005 10.5 18.0005Z"
            fill="#C4C6C9"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M14.0649 7.56588C14.3773 7.25346 14.3773 6.74693 14.0649 6.43451C13.7525 6.12209 13.2459 6.12209 12.9335 6.43451L10.4991 8.86891L8.0649 6.4347C7.75248 6.12228 7.24595 6.12228 6.93353 6.4347C6.62111 6.74712 6.62111 7.25365 6.93353 7.56607L9.36774 10.0003L6.93361 12.4344C6.62119 12.7468 6.62119 13.2534 6.93361 13.5658C7.24603 13.8782 7.75256 13.8782 8.06498 13.5658L10.4991 11.1316L12.9334 13.566C13.2458 13.8784 13.7524 13.8784 14.0648 13.566C14.3772 13.2535 14.3772 12.747 14.0648 12.4346L11.6305 10.0003L14.0649 7.56588Z"
            fill="white"
          />
        </svg>
      )}
      {variant === CloseIconVariant.INPUT_CLEAR && (
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M11.9999 21.5999C6.7199 21.5999 2.3999 17.2799 2.3999 11.9999C2.3999 6.7199 6.7199 2.3999 11.9999 2.3999C17.2799 2.3999 21.5999 6.7199 21.5999 11.9999C21.5999 17.2799 17.2799 21.5999 11.9999 21.5999V21.5999Z"
            fill={color || '#D8D8D8'}
          />
          <path
            d="M14.9208 7.72209C15.2957 7.34719 15.9043 7.3472 16.2792 7.72209C16.6541 8.09699 16.6541 8.70558 16.2792 9.08049L13.3583 12.0004L16.2792 14.9213C16.6541 15.2962 16.6541 15.9048 16.2792 16.2797C15.9043 16.6546 15.2957 16.6546 14.9208 16.2797L11.9999 13.3588L9.07903 16.2797C8.70413 16.6546 8.09651 16.6546 7.72161 16.2797C7.34672 15.9048 7.34671 15.2972 7.72161 14.9223L10.6425 12.0004L7.72161 9.07951C7.3467 8.70461 7.3467 8.09699 7.72161 7.72209C8.09651 7.34724 8.70414 7.3472 9.07903 7.72209L11.9999 10.642L14.9208 7.72209Z"
            fill="white"
          />
        </svg>
      )}
    </i>
  );
}

export default CloseIcon;
